import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useApiData } from '../../contexts/apiService';
import { useDataManager } from '../../contexts/dataManager';
import SQLTable from '../../components/sql_table/tableMain';
import type { TableColumnConfig } from '../../components/sql_table/tableMain';
import LabResultsGraph from './labResultsGraph';
import LabPdfUploadModal from './LabPdfUploadModal';
import LabPdfReviewModal from './LabPdfReviewModal';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import {
    buildPivotedRows,
    presentTestTypes,
    buildTestTypeDefMap,
    labelForTestType,
} from './labResultsHelpers';

// Tables this tab reads off apiData (rows, dropdown sources, spec limits, test catalog).
const REFERENCE_TABLES = [
    'lab_results',
    'lease_coordinates',
    'market_coordinates',
    'producer_definition',
    'market_quality_specs',
    'test_type_definitions',
];

const LabResults: React.FC = () => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { userRole = 'viewer' } = useUserAccess();
    const [sourceType, setSourceType] = useState<'lease' | 'market'>('lease');
    const [showGraph, setShowGraph] = useState<boolean>(false);
    const [pdfUploadOpen, setPdfUploadOpen] = useState<boolean>(false);
    const [pdfReviewPayload, setPdfReviewPayload] = useState<any | null>(null);
    // The uploaded PDF file, kept so the review modal can show it side-by-side.
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    // When true, the review modal opens empty for hand-entry (PDF unreadable / manual).
    const [reviewBlank, setReviewBlank] = useState<boolean>(false);

    // Tear down the review modal (also used as the ErrorBoundary reset handler so a
    // render crash can't trap the user behind a fallback).
    const closeReview = () => {
        setPdfReviewPayload(null);
        setReviewBlank(false);
        setPdfFile(null);
    };

    // Request supporting reference data (same guard pattern as scheduleTable_new /
    // tableMain: deps carry only what changes *which* tables to fetch — nothing here).
    useEffect(() => {
        REFERENCE_TABLES.forEach((t) => {
            if (!hasTableData(t)) requestTableData(t);
        });
    }, []);

    // Catalog of test types → curated labels/units for headers, dropdown and graph.
    const testTypeDefs = useMemo(
        () => buildTestTypeDefMap(apiData?.test_type_definitions),
        [apiData?.test_type_definitions],
    );

    // Raw EAV rows (for graph) and pivoted rows (for table display)
    const rawRows = useMemo(() => apiData?.lab_results || [], [apiData?.lab_results]);
    const filteredRawRows = useMemo(() => {
        if (sourceType === 'lease') {
            return rawRows.filter((r: any) => r.lease_id != null || r.lease_name);
        }
        return rawRows.filter((r: any) => r.market_id != null || r.market_name || r.station_name);
    }, [rawRows, sourceType]);
    const pivotedRows = useMemo(() => buildPivotedRows(filteredRawRows), [filteredRawRows]);

    // Build dynamic table columns: fixed identity columns + one column per
    // test_type present in the data. 
    const pivotedColumns: TableColumnConfig[] = useMemo(() => {
        const tests = presentTestTypes(pivotedRows, testTypeDefs);
        // A column is text vs numeric based on the pivoted values themselves
        // (buildPivotedRows stores text as a string, numbers as a number), so a
        // new test type classifies itself with no hardcoded list to maintain.
        const textTests = new Set<string>();
        for (const row of pivotedRows) {
            for (const tt of tests) {
                if (typeof row[tt] === 'string') textTests.add(tt);
            }
        }
        const baseCols: TableColumnConfig[] = [
            { colName: 'test_date', colHeader: 'Date', colType: 'date', editable: false },
            ...(sourceType === 'lease'
                ? [
                    { colName: 'lease_name', colHeader: 'Lease', colType: 'text' as const, editable: false },
                    { colName: 'producer_name', colHeader: 'Producer', colType: 'text' as const, editable: false },
                ]
                : [
                    { colName: 'station_name', colHeader: 'Station', colType: 'text' as const, editable: false },
                    { colName: 'market_name', colHeader: 'Market', colType: 'text' as const, editable: false },
                ]),
            { colName: 'sample_point', colHeader: 'Sample Point', colType: 'text', editable: false },
        ];
        const testCols: TableColumnConfig[] = tests.map((tt) => ({
            colName: tt,
            colHeader: labelForTestType(tt, testTypeDefs),
            colType: textTests.has(tt) ? 'text' : 'number',
            editable: false,
            decimals: textTests.has(tt) ? undefined : 2,
        }));
        const tailCols: TableColumnConfig[] = [
            { colName: 'lab_name', colHeader: 'Lab', colType: 'text', editable: false },
            { colName: 'sampled_by', colHeader: 'Sampled By', colType: 'text', editable: false },
            { colName: 'note', colHeader: 'Note', colType: 'text', editable: false },
            { colName: 'created_by', colHeader: 'Loaded By', colType: 'text', editable: false },
        ];
        return [...baseCols, ...testCols, ...tailCols];
    }, [pivotedRows, sourceType, testTypeDefs]);

    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= 1;
    const canUploadPdf = Number(USER_ROLE_LEVEL[userRole]) >= 2;

    return (
        <div className="labResults-container">
            <div className="labResults-topBar">
                <div className="labResults-toggle">
                    <button
                        className={`labResults-toggle-btn ${sourceType === 'lease' ? 'labResults-toggle-btn--active' : ''}`}
                        onClick={() => setSourceType('lease')}
                    >
                        Lease
                    </button>
                    <button
                        className={`labResults-toggle-btn ${sourceType === 'market' ? 'labResults-toggle-btn--active' : ''}`}
                        onClick={() => setSourceType('market')}
                    >
                        Market
                    </button>
                </div>
                {canUploadPdf && (
                    <button
                        className="labResults-toggle-btn"
                        onClick={() => setPdfUploadOpen(true)}
                        title="Upload a lab PDF and review extracted samples before saving"
                    >
                        Upload Lab PDF
                    </button>
                )}
                <button
                    className={`labResults-toggle-btn ${showGraph ? 'labResults-toggle-btn--active' : ''}`}
                    onClick={() => setShowGraph((v) => !v)}
                >
                    {showGraph ? 'Hide Graph' : 'Show Graph'}
                </button>
            </div>

            {pdfUploadOpen && (
                <LabPdfUploadModal
                    onClose={() => setPdfUploadOpen(false)}
                    onExtracted={(payload, file) => {
                        setPdfUploadOpen(false);
                        setReviewBlank(false);
                        setPdfFile(file);
                        setPdfReviewPayload(payload);
                    }}
                    onManualEntry={() => {
                        setPdfUploadOpen(false);
                        setReviewBlank(true);
                        setPdfFile(null);
                        setPdfReviewPayload({ pots: [] });
                    }}
                />
            )}

            {pdfReviewPayload && (
                <ErrorBoundary
                    onReset={closeReview}
                    fallbackRender={({ resetErrorBoundary }) => (
                        <div className="carrier-modal-overlay">
                            <div className="carrier-modal-content labPdf-review-modal">
                                <h2 className="labPdf-title">Something went wrong</h2>
                                <p className="labPdf-subtitle">
                                    The lab report review couldn't be displayed. Nothing was saved —
                                    please close this and try again, or enter the samples manually.
                                </p>
                                <div className="labPdf-actions">
                                    <button
                                        type="button"
                                        className="labPdf-btn labPdf-btn--primary"
                                        onClick={resetErrorBoundary}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                >
                    <LabPdfReviewModal
                        extractionResult={pdfReviewPayload}
                        pdfFile={pdfFile}
                        startBlank={reviewBlank}
                        defaultEntityType={sourceType}
                        onClose={closeReview}
                        onCommitted={closeReview}
                    />
                </ErrorBoundary>
            )}

            {showGraph && <LabResultsGraph tableData={filteredRawRows} qualitySpecs={apiData?.market_quality_specs || []} definitions={apiData?.test_type_definitions || []} />}

            <div className="labResults-tableWrapper">
                <SQLTable
                    tableName="lab_results"
                    tableData={pivotedRows}
                    idColumn="id"
                    columnConfigs={pivotedColumns}
                    showHeader={true}
                    selectableRows={false}
                    deletableRows={false}
                    enableFilters={true}
                    showTable={showTable}
                    userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
                />
            </div>
        </div>
    );
};

export default LabResults;
